/**
 * Serialization specialization - custom serializers for classes that cannot be directly modified.
 */
import * as THREE from 'three';
import {
  add as tslAdd,
  float as tslFloat,
  positionWorldDirection as tslPositionWorldDirection,
  sin as tslSin,
} from 'three/tsl';
import {
  AttributeNode,
  ConstNode,
  JoinNode,
  MeshBasicNodeMaterial,
  MeshLambertNodeMaterial,
  MeshMatcapNodeMaterial,
  MeshNormalNodeMaterial,
  MeshPhongNodeMaterial,
  MeshPhysicalNodeMaterial,
  MeshStandardNodeMaterial,
  MeshToonNodeMaterial,
  PointsNodeMaterial,
  SplitNode,
  SpriteNodeMaterial,
  SubBuildNode,
  UniformGroupNode,
  UniformNode,
  VarNode,
  VaryingNode,
} from 'three/webgpu';

import { MaterialUtils } from '../../materials.js';
import { UrlTexture } from '../../render/Texture.js';
import { MatcapShaderMaterial } from '../../runtime/materials/MatcapShaderMaterial.js';
import {
  ColorGradientNodeMaterialAsset,
  DissolveNodeMaterialAsset,
  FresnelRimNodeMaterialAsset,
  HeatDistortionNodeMaterialAsset,
  IridescenceNodeMaterialAsset,
  MeshBasicNodeMaterialAsset,
  MeshLambertNodeMaterialAsset,
  MeshMatcapNodeMaterialAsset,
  MeshNormalNodeMaterialAsset,
  MeshPhongNodeMaterialAsset,
  MeshPhysicalNodeMaterialAsset,
  MeshStandardNodeMaterialAsset,
  MeshToonNodeMaterialAsset,
  NeonPulseNodeMaterialAsset,
  ParallaxUVNodeMaterialAsset,
  PointsNodeMaterialAsset,
  SpriteNodeMaterialAsset,
  SSSNodeMaterialAsset,
  TransmissionNodeMaterialAsset,
  VertexWaveNodeMaterialAsset,
  WorldTriplanarNodeMaterialAsset,
} from '../../runtime/materials/nodeMaterialAssets/index.js';

import { registerClassAlias } from './clsAlias.js';
import {
  registerAsGlobalObject,
  registerPropertyMetadata,
  registerSimpleType,
  registerSpecialization,
  RegistrationFlags
} from './specializationUtils.js';

// MathNode and OperatorNode are not exported from three/webgpu — obtain them
// via instances so we have the actual constructor reference.
const MathNodeCls = ((tslSin(tslFloat(0)) as unknown) as VarNode<any, any>).node!.constructor as new (...a: any[]) => object;
const OperatorNodeCls = ((tslAdd(tslFloat(0), tslFloat(0)) as unknown) as VarNode<any, any>).node!.constructor as new (...a: any[]) => object;
// ShaderCallNodeInternal and ShaderNodeInternal are likewise internal.
const _posWD = tslPositionWorldDirection as unknown as VarNode<any, any>;
const ShaderCallNodeInternalCls = (_posWD.node as any).constructor as new (...a: any[]) => object;
const ShaderNodeInternalCls = (_posWD.node as any).shaderNode.constructor as new (...a: any[]) => object;


import type { PropertyMetadata } from './decorator.js';
import type { IDumper, ILoader } from './factories.js';

/**
 * How to specialize serialization for a class if it's not directly modifiable, i.e. from an external library or built-in class
 *
 * Method 1: Use `registerSpecialization` to register custom serialize/deserialize functions manually
 * arguments:
 *   cls: the class constructor
 *   serializeFn: function that takes an instance of the class and a Dumper, and uses the Dumper to serialize the instance
 *   deserializeFn: optional, function that takes an instance of the class and a Loader, and uses the Loader to load data into the instance
 *   staticDeserializeFn: optional, function that takes data and constructs and returns a new instance of the class
 *   clsAlias: optional, a short alias string to use instead of the full class name during serialization
 * Example:
 *   registerSpecialization({
 *     cls: SomeClass,
 *     // however you want to serialize the instance
 *     serializeFn: (obj: SomeClass, dumper: IDumper) => {
 *       dumper.dumpValue('f1', obj.f1);
 *       dumper.dumpValue('f23', [obj.f2, obj.f3]);
 *     },
 *    // however you want to deserialize into an existing instance
 *     deserializeFn: (obj: SomeClass, loader: ILoader) => {
 *       obj.f1 = loader.loadValue('f1');
 *       const [f2, f3] = loader.loadValue('f23');
 *       obj.f2 = f2;
 *       obj.f3 = f3;
 *     },
 *     // however you want to use `data` to construct a new instance
 *     staticDeserializeFn: (data: any) => new SomeClass({f1: data.f1, f2: data.f23[0], f3: data.f23[1]}),
 *     clsAlias: 'sc'
 *   });
 *
 *
 * Method 2: Use `registerSimpleType` to register classes that can be serialized/deserialized into/from a simple array of values
 *           T() <==> [p1, p2, p3, ...], where T is a class containing properties p1, p2, p3, and T can be constructed as new `T(p1, p2, p3, ...)`
 *           i.e. THREE.Color <==> [r, g, b],
 *           in fact it's just a shorthand for `registerSpecialization`
 * arguments:
 *   cls: the class constructor
 *   propertiesGetter: function that takes an instance of the class and returns the properties that should be serialized/deserialized as an array
 *                     it's taking a function instead of an array of strings it allow compile time type checking of the properties,
 *                     this is especially useful for o.params.xxx that has multiple levels
 *   clsAlias: optional, a short alias string to use instead of the full class name during serialization
 *   flags: optional, RegistrationFlags to control whether to register deserializer/static deserializer
 * Example:
 *   registerSimpleType(THREE.Color, o => [o.r, o.g, o.b], 'c');  // serialize THREE.Color to {c: [r, g, b]}
 *
 * Method 3: Use `registerPropertyMetadata` to register property metadata for classes manually
 *           This is essentially the same as applying the `@property` decorator to each property,
 *           except that you don't have direct access to the class source code to add the decorators.
 *           This method can be used together with Method 1, as long as `dumper.dumpDefaults` and `loader.loadDefaults` are used in the
 *           serializeFn and deserializeFn respectively.
 * arguments:
 *   cls: the class constructor
 *   metadata: an object mapping property names to PropertyMetadata
 * Example:
 *   registerPropertyMetadata(SomeClass, {
 *     f1: {default: 0},
 *     f2: {},
 *     f3: {},
 *   });
 *   // this is equivalent to
 *   class SomeClass {
 *     @property({default: 0})
 *     public f1: number;
 *
 *     @property()
 *     public f2: number;
 *
 *     @property()
 *     public f3: number;
 *   }
 */


// JS typed arrays
registerSpecialization(
  {
    cls: Int32Array,
    serializeFn: (obj: Int32Array, dumper: IDumper) => { dumper.dumpValue('_', Array.from(obj)); },
    deserializeFn: (obj: Int32Array, loader: ILoader) => { obj.set(loader.loadValue('_').slice(0, obj.length)); },
    staticDeserializeFn: (data: any, loader: ILoader) => new Int32Array(data._),
    clsAlias: 'ia'
  }
);

registerSpecialization(
  {
    cls: Float32Array,
    serializeFn: (obj: Float32Array, dumper: IDumper) => { dumper.dumpValue('_', Array.from(obj).map(v => Math.round(v * 1000000) / 1000000)); },
    deserializeFn: (obj: Float32Array, loader: ILoader) => { obj.set(loader.loadValue('_').slice(0, obj.length)); },
    staticDeserializeFn: (data: any, loader: ILoader) => new Float32Array(data._),
    clsAlias: 'fa'
  }
);

registerSpecialization(
  {
    cls: MatcapShaderMaterial,
    cdo: new MatcapShaderMaterial({ matcap: new THREE.Texture() }),
    staticDeserializeFn: MatcapShaderMaterial.staticDeserialize
  }
);

// Math
registerSimpleType(THREE.Color, o => [o.r, o.g, o.b], { clsAlias: 'c' });
registerSimpleType(THREE.Euler, o => [o.x, o.y, o.z], { clsAlias: 'e' });
registerSimpleType(THREE.Quaternion, o => [o.x, o.y, o.z, o.w], { clsAlias: 'q' });
registerSimpleType(THREE.Vector2, o => [o.x, o.y], { clsAlias: 'v2' });
registerSimpleType(THREE.Vector3, o => [o.x, o.y, o.z], { clsAlias: 'v3' });
registerSimpleType(THREE.Vector4, o => [o.x, o.y, o.z, o.w], { clsAlias: 'v4' });
registerSimpleType(THREE.Matrix2, o => o.elements, { clsAlias: 'm2' });
registerSimpleType(THREE.Matrix3, o => o.elements, { clsAlias: 'm3' });
registerSimpleType(THREE.Matrix4, o => o.elements, { clsAlias: 'm4' });


// dumping/loading non-primitive types. it works, but is that a good idea
registerSimpleType(THREE.Box2, o => [o.min, o.max], { clsAlias: 'b2' });
registerSimpleType(THREE.Box3, o => [o.min, o.max], { clsAlias: 'b3' });
registerSimpleType(THREE.Sphere, o => [o.center, o.radius]);
registerSimpleType(THREE.Plane, o => [o.normal, o.constant]);


// geometries
// Not allow geometry to "populate" an existing instance during deserialization, because geometries are complex objects that do not consist only of the serialized properties
registerSpecialization({
  cls: THREE.BufferGeometry,
  disallowPopulate: true
});

registerSimpleType(THREE.BoxGeometry, o => [o.parameters.width, o.parameters.height, o.parameters.depth], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.SphereGeometry, o => [o.parameters.radius, o.parameters.widthSegments, o.parameters.heightSegments], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.CylinderGeometry, o => [o.parameters.radiusTop, o.parameters.radiusBottom, o.parameters.height, o.parameters.radialSegments, o.parameters.heightSegments, o.parameters.openEnded], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.PlaneGeometry, o => [o.parameters.width, o.parameters.height, o.parameters.widthSegments, o.parameters.heightSegments], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.CapsuleGeometry, o => [o.parameters.radius, o.parameters.height, o.parameters.capSegments, o.parameters.radialSegments], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.ConeGeometry, o => [o.parameters.radius, o.parameters.height, o.parameters.radialSegments, o.parameters.heightSegments], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.CircleGeometry, o => [o.parameters.radius, o.parameters.segments, o.parameters.thetaStart, o.parameters.thetaLength], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.TorusGeometry, o => [o.parameters.radius, o.parameters.tube, o.parameters.radialSegments, o.parameters.tubularSegments], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.TorusKnotGeometry, o => [o.parameters.radius, o.parameters.tube, o.parameters.radialSegments, o.parameters.tubularSegments, o.parameters.radialSegments, o.parameters.tubularSegments], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.TubeGeometry, o => [o.parameters.path, o.parameters.tubularSegments, o.parameters.radius, o.parameters.radialSegments, o.parameters.closed], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.DodecahedronGeometry, o => [o.parameters.radius, o.parameters.detail], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.EdgesGeometry, o => [o.parameters.geometry, o.parameters.thresholdAngle], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.ExtrudeGeometry, o => [o.parameters.shapes, o.parameters.options], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.IcosahedronGeometry, o => [o.parameters.radius, o.parameters.detail], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.LatheGeometry, o => [o.parameters.points, o.parameters.segments, o.parameters.phiStart, o.parameters.phiLength], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.OctahedronGeometry, o => [o.parameters.radius, o.parameters.detail], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.PolyhedronGeometry, o => [o.parameters.vertices, o.parameters.indices, o.parameters.radius, o.parameters.detail], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.RingGeometry, o => [o.parameters.innerRadius, o.parameters.outerRadius, o.parameters.thetaSegments, o.parameters.phiSegments], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.ShapeGeometry, o => [o.parameters.shapes, o.parameters.curveSegments], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.TetrahedronGeometry, o => [o.parameters.radius, o.parameters.detail], { flags: RegistrationFlags.NoDeserializer });
registerSimpleType(THREE.WireframeGeometry, o => [o.parameters.geometry], { flags: RegistrationFlags.NoDeserializer });


// lights
registerSimpleType(THREE.AmbientLight, o => [o.color, o.intensity]);
registerSimpleType(THREE.DirectionalLight, o => [o.color, o.intensity]);
registerSimpleType(THREE.HemisphereLight, o => [o.color, o.groundColor, o.intensity]);
registerSimpleType(THREE.PointLight, o => [o.color, o.intensity, o.distance, o.decay]);
registerSimpleType(THREE.LightProbe, o => [o.sh, o.intensity]);
registerSimpleType(THREE.RectAreaLight, o => [o.color, o.intensity, o.width, o.height]);
registerSimpleType(THREE.SpotLight, o => [o.color, o.intensity, o.distance, o.angle, o.penumbra, o.decay]);

// fog
registerSimpleType(THREE.Fog, o => [o.color, o.near, o.far]);
registerSimpleType(THREE.FogExp2, o => [o.color, o.density]);

// material classes
function registerMaterial<T>(cls: (new () => T) & Function) {
  const proto = new cls() as Record<keyof T, any>;
  const metadata: Partial<Record<keyof T, PropertyMetadata>> = {};

  for (const [k, v] of Object.entries(proto)) {
    const meta: PropertyMetadata = {};
    if (k === 'uuid') {
      // uuid is not supposed to be serialized or have a default value
      meta.transientOnPrefab = true;
    } else if (k === 'version') {
      continue;  // skip version property
    } else if (k === 'type') {
      continue;  // skip type property - redundant with class name ($bc)
    }
    else if (v === null || typeof v !== 'object') {
      meta.default = v as number | string | boolean | null | undefined;
    }
    metadata[k as keyof T] = meta;
  }

  registerPropertyMetadata(cls, metadata);
  // set the default object ignoring the ctor arguments
  // because the all seem to have a constructor of `constructor(params?: ParamType)` form
  // and that evaluates to 1 arg, and serializer won't do `new cls()`
  registerSpecialization({
    cls,
    cdo: new cls(),
    deserializeFn: (material: any, loader: ILoader) => {
      // Default deserialization
      loader.loadProperties(material);
      if (material.userData) {
        delete material.userData.colorSpaceFixed;
      }

      // Migration: apply legacy userData.textureTransforms to actual textures
      if (material.userData?.textureTransforms) {
        const transforms = material.userData.textureTransforms;
        for (const [textureProp, settings] of Object.entries(transforms)) {
          const texture = material[textureProp];
          if (texture instanceof THREE.Texture && typeof settings === 'object' && settings !== null) {
            const s = settings as any;
            if (s.wrapS !== undefined) {
              texture.wrapS = s.wrapS;
            }
            if (s.wrapT !== undefined) {
              texture.wrapT = s.wrapT;
            }
            if (s.repeatX !== undefined || s.repeatY !== undefined) {
              texture.repeat.set(s.repeatX ?? 1, s.repeatY ?? 1);
            }
            if (s.offsetX !== undefined || s.offsetY !== undefined) {
              texture.offset.set(s.offsetX ?? 0, s.offsetY ?? 0);
            }
            if (s.rotation !== undefined) {
              texture.rotation = s.rotation;
            }
            texture.needsUpdate = true;
          }
        }
        // Remove legacy data
        delete material.userData.textureTransforms;
      }
    }
  });
}

registerMaterial(THREE.MeshStandardMaterial);
registerMaterial(THREE.LineBasicMaterial);
registerMaterial(THREE.LineDashedMaterial);
registerMaterial(THREE.MeshBasicMaterial);
registerMaterial(THREE.MeshDepthMaterial);
registerMaterial(THREE.MeshDistanceMaterial);
registerMaterial(THREE.MeshLambertMaterial);
registerMaterial(THREE.MeshMatcapMaterial);
registerMaterial(THREE.MeshNormalMaterial);
registerMaterial(THREE.MeshPhongMaterial);
registerMaterial(THREE.MeshPhysicalMaterial);
registerMaterial(THREE.MeshToonMaterial);
registerMaterial(THREE.RawShaderMaterial);
registerMaterial(THREE.ShaderMaterial);
registerMaterial(THREE.ShadowMaterial);
registerMaterial(THREE.PointsMaterial);
registerMaterial(THREE.SpriteMaterial);

// ─── WebGPU node materials ────────────────────────────────────────────────────
// Keep class aliases for the base Mesh*NodeMaterial types so any legacy assets
// that were serialized with the old $bc values can still be resolved during
// loading. We no longer call registerMaterial() on them — the *NodeMaterialAsset
// subclasses own their own serialize/staticDeserialize and are the canonical asset types.
const LEGACY_NODE_MATERIAL_ALIASES: Array<[new () => THREE.Material, string]> = [
  [MeshBasicNodeMaterial as unknown as new () => THREE.Material, 'WEBGPU.MeshBasicNodeMaterial'],
  [MeshLambertNodeMaterial as unknown as new () => THREE.Material, 'WEBGPU.MeshLambertNodeMaterial'],
  [MeshPhongNodeMaterial as unknown as new () => THREE.Material, 'WEBGPU.MeshPhongNodeMaterial'],
  [MeshStandardNodeMaterial as unknown as new () => THREE.Material, 'WEBGPU.MeshStandardNodeMaterial'],
  [MeshPhysicalNodeMaterial as unknown as new () => THREE.Material, 'WEBGPU.MeshPhysicalNodeMaterial'],
  [MeshToonNodeMaterial as unknown as new () => THREE.Material, 'WEBGPU.MeshToonNodeMaterial'],
  [MeshMatcapNodeMaterial as unknown as new () => THREE.Material, 'WEBGPU.MeshMatcapNodeMaterial'],
  [MeshNormalNodeMaterial as unknown as new () => THREE.Material, 'WEBGPU.MeshNormalNodeMaterial'],
  [SpriteNodeMaterial as unknown as new () => THREE.Material, 'WEBGPU.SpriteNodeMaterial'],
  [PointsNodeMaterial as unknown as new () => THREE.Material, 'WEBGPU.PointsNodeMaterial'],
];
for (const [cls, alias] of LEGACY_NODE_MATERIAL_ALIASES) {
  registerClassAlias(cls as any, alias);
}

// ─── Node-material asset specializations ──────────────────────────────────────
// These classes own their serialize/staticDeserialize; we register them in the
// serialization registry so the Dumper/Loader can dispatch to them.
const NODE_MATERIAL_ASSET_CLASSES: Array<new () => THREE.Material> = [
  MeshBasicNodeMaterialAsset as unknown as new () => THREE.Material,
  MeshLambertNodeMaterialAsset as unknown as new () => THREE.Material,
  MeshPhongNodeMaterialAsset as unknown as new () => THREE.Material,
  MeshStandardNodeMaterialAsset as unknown as new () => THREE.Material,
  MeshPhysicalNodeMaterialAsset as unknown as new () => THREE.Material,
  MeshToonNodeMaterialAsset as unknown as new () => THREE.Material,
  MeshMatcapNodeMaterialAsset as unknown as new () => THREE.Material,
  MeshNormalNodeMaterialAsset as unknown as new () => THREE.Material,
  SpriteNodeMaterialAsset as unknown as new () => THREE.Material,
  PointsNodeMaterialAsset as unknown as new () => THREE.Material,
  FresnelRimNodeMaterialAsset as unknown as new () => THREE.Material,
  VertexWaveNodeMaterialAsset as unknown as new () => THREE.Material,
  DissolveNodeMaterialAsset as unknown as new () => THREE.Material,
  ColorGradientNodeMaterialAsset as unknown as new () => THREE.Material,
  NeonPulseNodeMaterialAsset as unknown as new () => THREE.Material,
  IridescenceNodeMaterialAsset as unknown as new () => THREE.Material,
  ParallaxUVNodeMaterialAsset as unknown as new () => THREE.Material,
  TransmissionNodeMaterialAsset as unknown as new () => THREE.Material,
  WorldTriplanarNodeMaterialAsset as unknown as new () => THREE.Material,
  HeatDistortionNodeMaterialAsset as unknown as new () => THREE.Material,
  SSSNodeMaterialAsset as unknown as new () => THREE.Material,
];
for (const cls of NODE_MATERIAL_ASSET_CLASSES) {
  registerSpecialization({
    cls,
    serializeFn: (obj: any, dumper) => obj.serialize(dumper),
    staticDeserializeFn: (data: any, loader) => (cls as any).staticDeserialize(data, loader),
    cdo: new cls(),
  });
}

// ─── TSL node graph classes ───────────────────────────────────────────────────
// These need class aliases so the serializer can look them up by name.
// Classes exported from three/webgpu:
const registerMaterialNode = (cls: any, alias?: string) => {
  registerSpecialization(
    {
      cls,
      serializeFn: (obj: any, dumper: IDumper) => {
        const uuid = obj._uuid;
        if (uuid !== undefined) {
          // remove the uuid for serialization
          delete obj._uuid;
        }
        dumper.dumpProperties(obj);
        if (uuid) {
          obj._uuid = uuid;
        }
      },
      clsAlias: alias ?? `WEBGPU.${cls.name}`
    }
  );
};

registerMaterialNode(VarNode,);
registerMaterialNode(ConstNode,);
registerMaterialNode(JoinNode,);
registerMaterialNode(SplitNode,);
registerMaterialNode(AttributeNode,);
registerMaterialNode(VaryingNode,);
registerMaterialNode(UniformNode,);
registerMaterialNode(UniformGroupNode,);
registerMaterialNode(SubBuildNode,);
// Classes not exported from three/webgpu — obtained via instances:
registerMaterialNode(MathNodeCls as any, 'WEBGPU.MathNode');
registerMaterialNode(OperatorNodeCls as any, 'WEBGPU.OperatorNode');
registerMaterialNode(ShaderCallNodeInternalCls as any, 'WEBGPU.ShaderCallNodeInternal');
registerMaterialNode(ShaderNodeInternalCls as any, 'WEBGPU.ShaderNodeInternal');


registerPropertyMetadata(THREE.Object3D, {
  uuid: { transientOnPrefab: true, readonly: true, order: -20 },
  name: { order: -19, hidden: true },
  position: { order: -10 },
  rotation: { order: -10 },
  scale: { order: -10 },
  visible: { default: true, order: -5 },
  castShadow: { default: false, order: 0 },
  receiveShadow: { default: false, order: 0 },
  frustumCulled: { default: true, order: 0 },
  renderOrder: { default: 0, order: 0 },
  up: { order: 50 },
  userData: { hidden: true },
});

registerPropertyMetadata(UrlTexture, {
  mapping: {},
  wrapS: {},
  wrapT: {},
  magFilter: {},
  minFilter: {},
  format: {},
  type: {},
  anisotropy: {},
  colorSpace: {},
  repeat: {},
  offset: {},
  rotation: {},
});


// external objects that should be globally shared (lazy registration to defer THREE.js object creation)
registerAsGlobalObject('DefaultTexture', () => MaterialUtils.DefaultTexture);
registerAsGlobalObject('DefaultMaterial', () => MaterialUtils.DefaultMaterial);
